import { dbManagerManifest } from './db-manager/manifest';
import { dbManagerEntrySource } from './db-manager/entry';
import { taskManagerManifest } from './task-manager/manifest';
import { taskManagerEntrySource } from './task-manager/entry';
import { documentsManifest } from './documents/manifest';
import { documentsEntrySource } from './documents/entry';
import { chatManifest } from './chat/manifest';
import { chatEntrySource } from './chat/entry';
import { vaultManifest } from './vault/manifest';
import { vaultEntrySource } from './vault/entry';
import { ftpManifest } from './ftp/manifest';
import { ftpEntrySource } from './ftp/entry';
import { terminalManifest } from './terminal/manifest';
import { terminalEntrySource } from './terminal/entry';
import { calendarManifest } from './calendar/manifest';
import { calendarEntrySource } from './calendar/entry';
import { mailCatcherManifest } from './mail-catcher/manifest';
import { mailCatcherEntrySource } from './mail-catcher/entry';
import { projectsManifest } from './projects/manifest';
import { projectsEntrySource } from './projects/entry';
import { graphManifest } from './graph/manifest';
import { graphEntrySource } from './graph/entry';

/**
 * Curated first-party bundled extensions — the PRODUCTION delivery path
 * (Phase 6). These ship compiled into the app binary and are registered
 * unconditionally by bootstrap.js through the exact same registry/runtime
 * contract a marketplace download uses: `{ manifest, entrySource,
 * grantedCapabilities }`. Bundled = build-time registration; marketplace
 * (feature-gated off, see utils/featureFlags.js `marketplaceEnabled`) =
 * runtime loading of the same shape. Turning the marketplace on later is a
 * loader swap, not a rewrite.
 *
 * DELIBERATE CONSENT-MODEL CHANGE (documented per the Phase 6 brief):
 * first-party bundled extensions are trusted — their capabilities are
 * auto-granted straight from the manifest's declared `permissions`, with no
 * consent screen. Persisted user state still wins on every launch:
 * bootstrap.js prefers the rehydrated `enabled` / `grantedCapabilities`
 * values over these defaults, so a user's disable or permission-revoke in
 * Settings > Extensions survives restarts.
 *
 * GUARDRAIL: only curated first-party extensions belong in this list —
 * auto-granting is exactly the widened trust the marketplace consent screen
 * exists to prevent for third-party code. Dev-only extensions
 * stay in dev/localExtensions.js behind the REACT_APP_BUNDLED_EXTENSIONS dev
 * flag and must not be added here.
 */
const autoGrant = (manifest) => (manifest.permissions || []).map((p) => p.capability);

export const bundledExtensions = [
  {
    manifest: taskManagerManifest,
    entrySource: taskManagerEntrySource,
    grantedCapabilities: autoGrant(taskManagerManifest),
  },
  {
    manifest: documentsManifest,
    entrySource: documentsEntrySource,
    grantedCapabilities: autoGrant(documentsManifest),
  },
  {
    manifest: chatManifest,
    entrySource: chatEntrySource,
    grantedCapabilities: autoGrant(chatManifest),
  },
  {
    manifest: vaultManifest,
    entrySource: vaultEntrySource,
    grantedCapabilities: autoGrant(vaultManifest),
  },
  {
    manifest: ftpManifest,
    entrySource: ftpEntrySource,
    grantedCapabilities: autoGrant(ftpManifest),
  },
  {
    manifest: terminalManifest,
    entrySource: terminalEntrySource,
    grantedCapabilities: autoGrant(terminalManifest),
  },
  {
    manifest: dbManagerManifest,
    entrySource: dbManagerEntrySource,
    grantedCapabilities: autoGrant(dbManagerManifest),
  },
  {
    manifest: calendarManifest,
    entrySource: calendarEntrySource,
    grantedCapabilities: autoGrant(calendarManifest),
  },
  {
    manifest: mailCatcherManifest,
    entrySource: mailCatcherEntrySource,
    grantedCapabilities: autoGrant(mailCatcherManifest),
  },
  {
    manifest: projectsManifest,
    entrySource: projectsEntrySource,
    grantedCapabilities: autoGrant(projectsManifest),
  },
  {
    manifest: graphManifest,
    entrySource: graphEntrySource,
    grantedCapabilities: autoGrant(graphManifest),
  },
];
